/**
 * The inbox's left column: one row per submission waiting, the open one marked.
 *
 * ── A row is ONE target ──────────────────────────────────────────────────────────────────────
 * Since 4 November 2026 the row opens its submission in the pane beside the list, and the pane is
 * where it is marked reviewed — so the eye and the tick each row used to carry went with the
 * dialog they opened. The row is a single button carrying `aria-current`; the accent bar and the
 * fill are the second channel, never the only one.
 *
 * ── The dot marks a submission that came ON TIME ─────────────────────────────────────────────
 * Owner, 4 November 2026: „kropka przydatna i czytelna". It is never the only channel: a late one
 * says how late in words under its day, and an on-time one is named so for a listener. An intake
 * has no term to keep, so it wears neither (`ReviewRow.onTime`).
 */

import { useTranslation } from 'react-i18next';

import { AvatarChip } from '~/shared/ui/AvatarChip';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ReviewRow } from '../model/reviewQueue';
import { reviewRowId } from '../lib/useReviewTab';
import styles from './ReviewQueue.module.css';

interface ReviewQueueListProps {
  readonly rows: readonly ReviewRow[];
  readonly openedId: number | null;
  readonly onOpen: (assignmentId: number) => void;
  readonly reveal: string;
}

export function ReviewQueueList({ rows, openedId, onOpen, reveal }: ReviewQueueListProps) {
  const { t } = useTranslation();

  return (
    <ul className={`${String(styles.list)} ${reveal}`} aria-label={t('web:reports.queue.label')}>
      {rows.map((row) => {
        const on = row.assignmentId === openedId;
        return (
          <li key={row.assignmentId} className={`${String(styles.row)} ${on ? String(styles.rowOn) : ''}`}>
            <button
              type="button"
              id={reviewRowId(row.assignmentId)}
              className={styles.rowButton}
              aria-current={on ? 'true' : undefined}
              onClick={() => {
                onOpen(row.assignmentId);
              }}
            >
              <AvatarChip initials={row.initials} avatarUrl={row.avatarUrl} loading="lazy" />
              <span className={styles.rowText}>
                <span className={styles.rowTop}>
                  <span className={styles.rowName}>{row.name}</span>
                  <span className={styles.rowForm}>{row.formShort}</span>
                </span>
                <span className={styles.rowSummary}>{row.summary}</span>
              </span>
              <span className={styles.rowWhen}>
                <span className={styles.rowDay}>
                  {/* A check-in that kept its term. An intake has none, so it gets neither. */}
                  {row.onTime ? <i className={styles.pip} aria-hidden /> : null}
                  {row.when}
                  {row.onTime ? (
                    <span className={hidden.srOnly}>, {t('web:reports.queue.onTimeMark')}</span>
                  ) : null}
                </span>
                {row.lateness === null ? null : (
                  <span className={styles.rowLate}>{row.lateness}</span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
